import { dataFetcher, endpoints } from 'Api';

/**
 * New-user onboarding checklist — "explored sections" tracking.
 *
 * The backend owns this state. Whenever the user actually does one of the
 * onboarding actions we POST /updateExplored with a fixed section key, and the
 * key is added to the `explored` array that /getMyProfile returns. The dashboard
 * banner ticks an item off when its key appears in that array.
 *
 * The section keys below are a contract with the backend — they are stored
 * verbatim in `explored`, so they must never be renamed.
 */
export const EXPLORED_SECTION = {
  upload: 'upload',
  invite: 'invite',
  rearrange: 'rearrange',
  story: 'tellyourstory',
  addCard: 'addcard',
};

const VALID_SECTIONS = Object.values(EXPLORED_SECTION);

// Fired on the window after a section is marked, so a mounted checklist can tick
// over immediately instead of waiting for the next profile refresh.
export const ONBOARDING_EVENT = 'projector:onboarding-checklist-updated';

/**
 * Optimistic local mirror of the server's `explored` array. It exists purely so
 * the checkbox flips the moment the user finishes an action — /getMyProfile is
 * still the source of truth and its list is merged on top on every read.
 */
const CACHE_KEY = 'onboardingExplored';

const scopedCacheKey = () => {
  let userId = '';
  try {
    userId = localStorage.getItem('id') || '';
  } catch (e) { /* storage unavailable */ }
  return `${CACHE_KEY}:${userId || 'anon'}`;
};

/**
 * The API hands `explored` back as a JSON-encoded string ("[\"upload\"]"), but
 * tolerate a plain array or a comma list too — this is display state, it should
 * never throw.
 */
export const parseExploredSections = (explored) => {
  if (!explored) return [];
  if (Array.isArray(explored)) return explored.filter((s) => typeof s === 'string');
  if (typeof explored !== 'string') return [];
  try {
    const parsed = JSON.parse(explored);
    if (Array.isArray(parsed)) return parsed.filter((s) => typeof s === 'string');
  } catch (e) { /* not JSON — fall through */ }
  return explored.split(',').map((s) => s.trim()).filter(Boolean);
};

const readCache = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(scopedCacheKey()) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeCache = (sections) => {
  try {
    localStorage.setItem(scopedCacheKey(), JSON.stringify(sections));
  } catch (e) { /* storage unavailable / quota — the server still has it */ }
};

/** Sections we already know about, from the profile payload plus the local mirror. */
export const getExploredSections = (exploredFromProfile) => {
  const merged = new Set([...parseExploredSections(exploredFromProfile), ...readCache()]);
  return [...merged];
};

/**
 * Tell the backend the user has explored a section. Safe to call from anywhere
 * and safe to call repeatedly — repeats within a session are dropped, unknown
 * keys are ignored, and a failed request never surfaces to the user (the next
 * time they perform the action it will be retried).
 */
export const markSectionExplored = (section) => {
  if (!section || !VALID_SECTIONS.includes(section)) return Promise.resolve();

  const cached = readCache();
  if (cached.includes(section)) return Promise.resolve();

  // Mirror locally first so the checklist reacts immediately.
  writeCache([...cached, section]);
  try {
    window.dispatchEvent(new CustomEvent(ONBOARDING_EVENT));
  } catch (e) { /* older browsers without the CustomEvent constructor */ }

  return dataFetcher(endpoints.updateExplored, { section }).catch(() => {
    // Roll the mirror back so a later attempt retries the call.
    writeCache(readCache().filter((s) => s !== section));
  });
};

/**
 * Whether the user has dismissed the banner with its close button. Like the
 * collapsed flag this is cosmetic and per-browser, so it never goes to the
 * server — the explored sections are the real progress record.
 */
const DISMISSED_KEY = 'onboardingChecklistDismissed';

const scopedDismissedKey = () => {
  let userId = '';
  try {
    userId = localStorage.getItem('id') || '';
  } catch (e) { /* storage unavailable */ }
  return `${DISMISSED_KEY}:${userId || 'anon'}`;
};

/** @returns {boolean} whether the user has closed the banner for good. */
export const getOnboardingDismissed = () => {
  try {
    return localStorage.getItem(scopedDismissedKey()) === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Remember that the banner was closed, so it stays closed for the rest of this
 * session (reloads and route changes included). It is deliberately NOT for
 * good: logging out clears it (see clearOnboardingBannerState) so the next
 * login reminds the user about whatever they still haven't explored.
 */
export const setOnboardingDismissed = (dismissed) => {
  try {
    localStorage.setItem(scopedDismissedKey(), dismissed === true ? 'true' : 'false');
  } catch (e) { /* storage unavailable — it reappears on the next load */ }
};

/**
 * Forget "closed" — called from the logout path, so signing in again shows the
 * banner afresh with whatever is still outstanding. Every user's keys are
 * cleared, not just the one whose id is still in storage, because the various
 * logout paths drop `id` at different points.
 *
 * The explored sections are progress, not chrome, so they are left alone (the
 * server owns them anyway).
 */
export const clearOnboardingBannerState = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(`${DISMISSED_KEY}:`))
      .forEach((key) => localStorage.removeItem(key));
  } catch (e) { /* storage unavailable — nothing was stored either */ }
};
